import { createContext, forwardRef, useCallback, useMemo, useRef } from 'react';
import { type AriaRadioProps, useRadio, useRadioGroup } from 'react-aria';
import { type RadioGroupProps, type RadioGroupState, useRadioGroupState } from 'react-stately';
import cn from 'classnames';
import { Card, HStack, Stub, Text, VStack } from '@direct-frontend/components';
import { type OrderDeliveryOptions } from '@/shared/api/order';
import { Checkbox } from '@/shared/components/Checkbox';
import { formatDeliveryDate } from '@/shared/lib/datetime';
import { formatPrice } from '@/shared/lib/formatPrice';
import { useRadioState } from '@/shared/lib/hooks';
import { useDeliveryOptions } from './lib/useDeliveryOptions';
import styles from './DeliveryOptionsInput.module.css';

const RadioContext = createContext<RadioGroupState | null>(null);

interface DeliveryOptionProps extends AriaRadioProps {
    isLoading: boolean;
    title: string;
    caption?: string;
    price: string | null;
    interval: { from: number; to: number } | null;
    validationState: 'valid' | 'invalid';
    isDefinitely?: boolean;
}

const DeliveryOptionBox = forwardRef<HTMLDivElement, DeliveryOptionProps>(
    function DeliveryOptionBox(props, forwardedRef) {
        const { title, price, interval, validationState, isDisabled, isLoading } = props;
        const ref = useRef(null);
        const state = useRadioState(RadioContext);
        const { inputProps, labelProps } = useRadio(props, state, ref);
        const isFree = useMemo(() => (price ? parseFloat(price) === 0 : true), [price]);

        const formattedPrice = useMemo(() => {
            if (typeof price !== 'string') {
                return '';
            }

            if (isFree) {
                return 'Бесплатно';
            }

            return `от ${formatPrice(price)}`;
        }, [price, isFree]);

        const getContent = () => {
            if (isLoading) {
                return (
                    <VStack gap="4" className={styles.optionInfo}>
                        <Stub height={12} width={70} />
                        <Stub height={12} width={42} />
                    </VStack>
                );
            }

            if (typeof price === 'string') {
                return (
                    <VStack className={styles.optionInfo}>
                        {interval ? (
                            <Text size="xs" role="supplementary">
                                {formatDeliveryDate(interval.from)}
                            </Text>
                        ) : (
                            <Text size="xs" role="supplementary">
                                Завтра или позже
                            </Text>
                        )}
                        <Text size="xs" role="supplementary" color={isFree ? 'green' : 'gray'}>
                            {formattedPrice}
                        </Text>
                    </VStack>
                );
            }

            return (
                <VStack className={styles.optionInfo}>
                    <Text size="xs" role="supplementary">
                        Недоступно
                    </Text>
                    <Text size="xs" role="supplementary">
                        в этом регионе
                    </Text>
                </VStack>
            );
        };

        return (
            <Card
                ref={forwardedRef}
                borderRadius="14"
                borderColor={validationState === 'invalid' ? 'red' : 'gray'}
                className={cn(
                    styles.option,
                    inputProps.checked && styles.option__checked,
                    isDisabled && styles.option__disabled
                )}
            >
                <label {...labelProps} className={styles.optionInner}>
                    <input {...inputProps} ref={ref} className={styles.optionInput} />
                    <Text role={isDisabled ? 'supplementary' : 'main'}>{title}</Text>
                    <HStack className={styles.optionFooter} gap="8" justifyContent="space-between" alignItems="center">
                        {getContent()}

                        <Checkbox checked={inputProps.checked} />
                    </HStack>
                </label>
            </Card>
        );
    }
);

interface DeliveryOptionsInputProps extends RadioGroupProps {
    options: OrderDeliveryOptions;
    isLoading: boolean;
    validationState: 'valid' | 'invalid';
}

export const DeliveryOptionsInput = (props: DeliveryOptionsInputProps) => {
    const { options, isLoading, validationState = 'valid' } = props;
    const refs = useRef<Record<string, HTMLDivElement | null>>({});
    const deliveryOptions = useDeliveryOptions(options);

    const onChange = useCallback(
        (value: string) => {
            refs.current[value]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            props.onChange?.(value);
        },
        [props]
    );

    const state = useRadioGroupState({ ...props, onChange });
    const { radioGroupProps } = useRadioGroup(props, state);

    return (
        <HStack className={styles.root} gap="12" {...radioGroupProps}>
            <RadioContext.Provider value={state}>
                {deliveryOptions
                    .filter((item) => item.isAvailable)
                    .map((item) => (
                        <DeliveryOptionBox
                            key={item.key}
                            ref={(el) => {
                                refs.current[item.key] = el;
                            }}
                            title={item.title}
                            aria-label={item.title}
                            isLoading={isLoading}
                            value={item.key}
                            isDisabled={isLoading}
                            interval={item.option?.interval ?? null}
                            price={item.option?.interval.price?.price ?? null}
                            validationState={validationState}
                        />
                    ))}
            </RadioContext.Provider>
        </HStack>
    );
};
